import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Simple encryption/decryption for client-side token protection
class SimpleEncryption {
  private static key = 'sigma08-key-2024'; // In production, this should be environment-specific

  static encrypt(text: string): string {
    try {
      // Simple XOR encryption for basic obfuscation
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ this.key.charCodeAt(i % this.key.length));
      }
      return btoa(result); // Base64 encode
    } catch {
      return text; // Fallback to plain text if encryption fails
    }
  }

  static decrypt(encryptedText: string): string {
    try {
      const decoded = atob(encryptedText); // Base64 decode
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ this.key.charCodeAt(i % this.key.length));
      }
      return result;
    } catch {
      return encryptedText; // Fallback if decryption fails (might be plain text)
    }
  }
}

// Token storage manager to handle race conditions and security
class TokenManager {
  private static instance: TokenManager;
  private pendingOperations = new Set<string>();

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private parseJWTPayload(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.parseJWTPayload(token);
    if (!payload || !payload.exp) return true;

    // Check if token expires within next 5 minutes
    const fiveMinutesFromNow = Math.floor(Date.now() / 1000) + 5 * 60;
    return payload.exp < fiveMinutesFromNow;
  }

  async setTokens(accessToken: string, refreshToken: string, rememberMe: boolean = false): Promise<void> {
    const operationId = `set-${Date.now()}`;
    if (this.pendingOperations.has(operationId)) return;

    this.pendingOperations.add(operationId);

    try {
      // Acquire cross-tab lock to prevent race conditions
      const lockKey = 'token_operation_lock';
      const lockValue = `${Date.now()}_${Math.random()}`;
      const lockExpiry = Date.now() + 5000; // 5 second timeout

      // Try to acquire lock with timeout
      const acquireLock = async (retries = 50): Promise<boolean> => {
        for (let i = 0; i < retries; i++) {
          const existingLock = localStorage.getItem(lockKey);
          if (!existingLock) {
            localStorage.setItem(lockKey, `${lockValue}_${lockExpiry}`);
            return true;
          }

          const [, , expiry] = existingLock.split('_');
          if (Date.now() >= parseInt(expiry)) {
            // Lock expired, try to acquire it
            localStorage.setItem(lockKey, `${lockValue}_${lockExpiry}`);
            return true;
          }

          // Wait briefly before retrying
          await new Promise(resolve => setTimeout(resolve, 10));
        }
        return false;
      };

      const lockAcquired = await acquireLock();
      if (!lockAcquired) {
        console.warn('Failed to acquire token operation lock, proceeding without lock');
      }

      try {
        // CRITICAL FIX: Store raw JWT tokens without encryption
        // The backend expects raw JWT tokens in Authorization headers
        // Encryption was causing authentication failures
        const timestamp = Date.now().toString();

        // ATOMIC OPERATION: Clear tokens from the NON-target storage first
        // This prevents inconsistent state where tokens exist in both storages
        // But DO NOT clear the target storage - we're about to write to it
        const targetStorage = rememberMe ? localStorage : sessionStorage;
        const otherStorage = rememberMe ? sessionStorage : localStorage;

        // Clear tokens from the other storage only
        otherStorage.removeItem('access_token');
        otherStorage.removeItem('refresh_token');
        otherStorage.removeItem('token_timestamp');
        otherStorage.removeItem('auth_token');
        otherStorage.removeItem('sigma08_jwt_token');

        // Set tokens in the target storage atomically
        targetStorage.setItem('access_token', accessToken); // Store raw JWT
        targetStorage.setItem('refresh_token', refreshToken); // Store raw JWT
        targetStorage.setItem('token_timestamp', timestamp);

        // BUGFIX: Also store as auth_token and sigma08_jwt_token for backward compatibility
        // Many components throughout the codebase look for these alternative keys
        targetStorage.setItem('auth_token', accessToken); // Store raw JWT
        targetStorage.setItem('sigma08_jwt_token', accessToken); // Store raw JWT
      } finally {
        // Always release lock if we acquired it
        if (lockAcquired) {
          const currentLock = localStorage.getItem(lockKey);
          if (currentLock && currentLock.startsWith(lockValue)) {
            localStorage.removeItem(lockKey);
          }
        }
      }
    } finally {
      this.pendingOperations.delete(operationId);
    }
  }

  getTokens(): {
    accessToken: string | null;
    refreshToken: string | null;
    source: 'session' | 'local' | null;
    isExpired?: boolean;
  } {
    try {
      // Check for token operation lock to avoid reading during write operations
      const lockKey = 'token_operation_lock';
      const existingLock = localStorage.getItem(lockKey);
      if (existingLock) {
        const [, , expiry] = existingLock.split('_');
        if (Date.now() < parseInt(expiry)) {
          // Operation in progress, wait briefly and retry
          setTimeout(() => {}, 10);
        }
      }

      // Priority: sessionStorage (temporary) > localStorage (persistent)
      const sessionAccess = sessionStorage.getItem('access_token');
      const sessionRefresh = sessionStorage.getItem('refresh_token');
      const sessionTimestamp = sessionStorage.getItem('token_timestamp');

      if (sessionAccess && sessionRefresh) {
        try {
          // CRITICAL FIX: Tokens are now stored as raw JWT, no decryption needed
          const decryptedAccess = sessionAccess;
          const decryptedRefresh = sessionRefresh;

          // Check if tokens are too old (24 hours for session storage)
          const tokenAge = Date.now() - parseInt(sessionTimestamp || '0');
          const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours

          if (tokenAge > maxSessionAge) {
            this.clearTokens();
            return { accessToken: null, refreshToken: null, source: null, isExpired: true };
          }

          const isExpired = this.isTokenExpired(decryptedAccess);
          return {
            accessToken: decryptedAccess,
            refreshToken: decryptedRefresh,
            source: 'session',
            isExpired,
          };
        } catch (decryptionError) {
          console.warn('Session token decryption failed:', decryptionError);
          // Fall through to check localStorage
        }
      }

      const localAccess = localStorage.getItem('access_token');
      const localRefresh = localStorage.getItem('refresh_token');
      const localTimestamp = localStorage.getItem('token_timestamp');

      if (localAccess && localRefresh) {
        try {
          // CRITICAL FIX: Tokens are now stored as raw JWT, no decryption needed
          const decryptedAccess = localAccess;
          const decryptedRefresh = localRefresh;

          // Check if tokens are too old (7 days for localStorage)
          const tokenAge = Date.now() - parseInt(localTimestamp || '0');
          const maxLocalAge = 7 * 24 * 60 * 60 * 1000; // 7 days

          if (tokenAge > maxLocalAge) {
            this.clearTokens();
            return { accessToken: null, refreshToken: null, source: null, isExpired: true };
          }

          const isExpired = this.isTokenExpired(decryptedAccess);
          return {
            accessToken: decryptedAccess,
            refreshToken: decryptedRefresh,
            source: 'local',
            isExpired,
          };
        } catch (decryptionError) {
          console.warn('Local token decryption failed:', decryptionError);
          // Clear invalid tokens
          this.clearTokens();
          return { accessToken: null, refreshToken: null, source: null, isExpired: true };
        }
      }

      // No valid tokens found
      return { accessToken: null, refreshToken: null, source: null };
    } catch (error) {
      console.warn('Error getting tokens:', error);
      // Return null state on any error to prevent app crashes
      return { accessToken: null, refreshToken: null, source: null };
    }
  }

  clearTokens(): void {
    try {
      // ATOMIC OPERATION: Clear all tokens from ALL locations
      // Use try-catch to ensure partial failures don't break the app

      // Clear from localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_timestamp');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sigma08_jwt_token'); // BUGFIX: Also clear sigma08_jwt_token

      // Clear from sessionStorage
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('token_timestamp');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('sigma08_jwt_token'); // BUGFIX: Also clear sigma08_jwt_token

      // Clear auth cookies by setting them to expire immediately
      const cookiesToClear = ['auth_token', 'access_token', 'refresh_token'];
      cookiesToClear.forEach(cookieName => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
      });

      // Clear any pending token refresh operations
      this.pendingOperations.clear();
    } catch (error) {
      console.warn('Error clearing tokens:', error);
      // Continue anyway - don't let storage errors break the logout flow
    }
  }
}

const tokenManager = TokenManager.getInstance();

export interface User {
  user_id: string;
  family_id: string;
  email?: string;
  role:
    | 'parent'
    | 'student'
    | 'teacher'
    | 'admin'
    | 'platform_admin'
    | 'creator'
    | 'individual'
    | 'school_admin'
    | 'district_admin'
    | 'principal'
    | 'enterprise_user';
  account_type?: 'individual' | 'family' | 'corporate' | 'school';
  permissions: string[];

  // Role-specific data
  family_name?: string;
  student_ids?: string[]; // For parents
  grade_level?: number; // For students
  age_group?: 'elementary' | 'middle_school' | 'high_school'; // For students
  subscription_status?: {
    plan_type: string;
    status: string;
    renewal_date?: string;
  };
  children?: Array<{
    student_id: string;
    grade_level: number;
    age_group: string;
  }>;

  // Creator-specific data
  creator_profile?: {
    content_categories: string[];
    approval_level: 'basic' | 'advanced' | 'expert';
    published_count: number;
  };
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,

      setUser: user => set({ user, error: null }),

      setLoading: isLoading => set({ isLoading }),

      setError: error => set({ error }),

      logout: async () => {
        try {
          // Step 1: Clear tokens using token manager to avoid race conditions
          tokenManager.clearTokens();

          // Step 2: Clear Zustand persisted state by explicitly setting user to null
          set({ user: null, error: null, isLoading: false });

          // Step 3: Send message to service worker to clear auth-related caches
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'CLEAR_CACHE',
            });
          }

          // Step 4: Clear all IndexedDB auth data
          try {
            const databases = await indexedDB.databases();
            for (const db of databases) {
              if (db.name && db.name.includes('sigma08')) {
                indexedDB.deleteDatabase(db.name);
              }
            }
          } catch (dbError) {
            console.warn('Could not clear IndexedDB:', dbError);
          }

          // Step 5: Wait briefly to ensure all operations complete before redirect
          await new Promise(resolve => setTimeout(resolve, 150));

          // Step 6: Force a full page reload to clear all runtime state
          // Using location.replace instead of href to avoid browser back button issues
          window.location.replace('/auth/login');
        } catch (error) {
          console.error('Error during logout:', error);
          // Always redirect even if there are errors - use replace to force clean state
          window.location.replace('/auth/login');
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'sigma08-auth',
      version: 2, // EPIC86 FIX: Increment version to force migration for account_type field
      partialize: (state): Partial<AuthState> => ({
        user: state.user,
      }),
      migrate: (persistedState: any, version: number) => {
        // Migration for version 2: Clear user state if account_type is missing
        // This forces a fresh profile fetch that includes account_type from backend
        if (version < 2) {
          console.log('[AuthStore Migration] Clearing persisted user state to fetch account_type');
          return {
            user: null,
          };
        }
        return persistedState;
      },
    }
  )
);

// Export token manager for use in services
export { tokenManager };
